import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";
import CTABanner from "@/components/CTABanner";
import { brand, frameworkArtwork, pageMeta } from "@/data/mockData";
import { Bold } from "lucide-react";

// ============================================================================
//   FRAMEWORK PAGE — White / elegant theme (matches the rest of the site).
//   The five uploaded infographics are showcased as-is inside light frames.
// ============================================================================

const claritySteps = [
  { letter: "C", word: "Clarify Objectives", desc: "Understanding each client's financial position, family priorities, timeline, and long-term property goals before recommending any strategy." },
  { letter: "L", word: "Leverage Market Intelligence", desc: "Using transaction trends, supply-demand dynamics, rental data, and entry analysis to identify stronger opportunities." },
  { letter: "A", word: "Assess Financial Risk", desc: "Reviewing affordability, loan exposure, liquidity, interest rate sensitivity, and downside protection for sustainable decision-making." },
  { letter: "R", word: "Roadmap Asset Progression", desc: "Planning strategic next steps based on upgrading opportunities, portfolio restructuring, retirement objectives, and wealth accumulation." },
  { letter: "I", word: "Identify Future Exit Potential", desc: "Evaluating resale demand, buyer demographics, holding horizon, and future marketability before entering any property purchase." },
  { letter: "T", word: "Tailor Personalised Strategies", desc: "Every recommendation customised based on life stage, income, family needs, investment objectives, and risk appetite." },
  { letter: "Y", word: "Yield Long-Term Outcomes", desc: "Focusing on sustainable capital appreciation, passive income, financial resilience, and intergenerational wealth planning." },
];

const waterPillars = [
  { letter: "W", word: "Wealth Foundation", desc: "Start with your first property. Build equity. Lay a strong financial foundation." },
  { letter: "A", word: "Asset Accumulation", desc: "Upgrade strategically. Move to the next asset that grows your wealth and lifestyle." },
  { letter: "T", word: "Time Leverage", desc: "Let time and property appreciation work together for compounding growth." },
  { letter: "E", word: "Earn Passive Income", desc: "Generate consistent rental income to support your lifestyle and retirement plans." },
  { letter: "R", word: "Retirement Freedom", desc: "Enjoy financial independence and the freedom to live life on your own terms." },
];

const clearSteps = [
  { letter: "C", word: "Create Clarity", tag: "Know Your Direction", desc: "Establish financial goals, affordability comfort zones, and long-term objectives before exploring opportunities." },
  { letter: "L", word: "List & Locate Pathways", tag: "See Your Options", desc: "Identify appropriate property options, upgrade routes, and investment pathways aligned with your goals." },
  { letter: "E", word: "Evaluate Opportunities", tag: "Make Informed Choices", desc: "Assess each option based on market positioning, financial sustainability, risk exposure, and growth potential." },
  { letter: "A", word: "Align Strategy & Goals", tag: "Match Your Life Plan", desc: "Ensure property decisions complement family priorities, lifestyle objectives, and retirement planning." },
  { letter: "R", word: "Reinforce Stability", tag: "Stay Resilient. Stay Ahead.", desc: "Stress-test plans with emergency preparedness, cash-flow considerations, and long-term survivability." },
];

const lexPillars = [
  { letter: "L", word: "Location Strength", desc: "Studying connectivity, transformation potential, surrounding developments, demand drivers, and long-term desirability." },
  { letter: "E", word: "Entry Advantage", desc: "Assessing whether the property is entering at a favourable valuation point with stronger upside potential and margin of safety." },
  { letter: "X", word: "Exit Strategy Planning", desc: "Understanding future resale demand, target buyer profiles, and potential liquidity before purchase." },
];

// Premium illustrated icons (reusing the existing advisory/services set for
// visual consistency with the rest of the site). Swap paths when the client
// supplies their own exact icon files.
const strategicServices = [
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/1.png", title: "HDB Upgrading Strategies", desc: "Time your MOP, optimise financing, and progress from HDB to private with structured planning.", icon: "fa-solid fa-arrow-trend-up" },
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/2.png", title: "Condo Investment Planning", desc: "Data-led acquisition focused on yield, capital growth, and exit liquidity.", icon: "fa-solid fa-building" },
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/3.png", title: "Portfolio Restructuring", desc: "Realign your property holdings to match your evolving life stage and long-term goals.", icon: "fa-solid fa-chart-pie" },
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/4.png", title: "Asset Progression Planning", desc: "Multi-stage roadmap that compounds equity across each property move.", icon: "fa-solid fa-chart-line" },
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/5.png", title: "Retirement-Focused Real Estate", desc: "Structure property income and equity to support a confident, stable retirement.", icon: "fa-solid fa-umbrella-beach" },
  { img: "/FrameworkPage_Strategic Property Wealth Planning-Section-icons/6.png", title: "Long-Term Property Wealth", desc: "Build sustainable capital growth and intergenerational wealth through disciplined strategy.", icon: "fa-regular fa-gem" },
];

const Artwork = ({ src, alt }) => (
  <div className="relative rounded-3xl overflow-hidden bg-white border border-gray-100 shadow-sm p-2 md:p-3">
    <img src={src} alt={alt} loading="lazy" className="w-full h-auto rounded-2xl" />
  </div>
);

const Eyebrow = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/60 font-semibold">
    {children}
  </div>
);

export default function Framework() {
  return (
    <>
      <SEO {...pageMeta.framework} />

      {/* =================== HERO =================== */}
      <section className="bg-white pt-16 pb-12 md:py-24" data-testid="framework-hero">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow>The C.L.A.R.I.T.Y Property System</Eyebrow>
            <h1 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-6xl text-[#0a2540] leading-[1.05]">
              Start Your <span className="italic">Property Planning</span> Journey.
            </h1>
            <p className="mt-6 text-base md:text-lg font-serif italic text-[#0a2540]/80">
              Clarity Today. Confidence Tomorrow. Freedom For Your Future.
            </p>
            <p className="mt-3 text-xs md:text-sm uppercase tracking-[0.25em] font-semibold text-[#0a2540]/70">
              A Proven System · A Clear Roadmap · A Better Future
            </p>
            <p className="mt-8 text-base text-slate-600 leading-relaxed max-w-3xl mx-auto">
              Make smarter property decisions through the integrated C.L.A.R.I.T.Y Property System — powered by the W.A.T.E.R Concept, C.L.E.A.R Framework and L.E.X Method, together with the M.E Roadmap.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                data-testid="framework-hero-book"
                className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-7 py-4 font-semibold text-base rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Book Strategy Consultation
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <a
                href="#clarity-system"
                data-testid="framework-hero-explore"
                className="inline-flex items-center gap-2 border border-[#0a2540]/20 text-[#0a2540] px-7 py-4 font-medium text-base rounded-full hover:border-[#0a2540] transition-all"
              >
                Explore The Framework
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="mt-14">
            <Artwork
              src={frameworkArtwork.journey}
              alt="Start Your Property Planning Journey — overview of the integrated C.L.A.R.I.T.Y Property System"
            />
          </Reveal>
        </div>
      </section>

      {/* =================== WHAT IS THE SYSTEM =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="framework-overview">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow>Strategic Property Wealth Planning</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              For Singapore <span className="italic">Homeowners</span> &amp; Investors
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-3xl mx-auto">
              At MeProperty, led by Johnny Tan, we help homeowners, HDB upgraders, families, and property investors in Singapore make clearer and more strategic property decisions through a structured advisory approach.
            </p>

            <h2 className="mt-14 font-serif text-3xl sm:text-4xl lg:text-[42px] font-bold text-[#0a2540] leading-[1.1]">
              The C.L.A.R.I.T.Y Property System
            </h2>

            <p className="mt-8 text-sm uppercase tracking-[0.18em] font-semibold text-[#0a2540]/70">
              Our approach focuses on helping clients understand:
            </p>

            <ul className="mt-6 space-y-3 inline-block text-left">
              {[
                ["Why", "a property move may matter today"],
                ["When", "the right timing window appears"],
                ["How", "to structure a sustainable property plan"],
                ["Which", "properties may offer stronger long-term positioning"],
                ["What", "risks should be assessed before committing"],
              ].map(([lead, rest]) => (
                <li key={lead} className="flex items-start gap-3 text-sm md:text-base text-slate-700">
                  <i className="fa-solid fa-circle-check mt-1 text-[#023e8a]" aria-hidden="true" />
                  <span><strong className="font-semibold text-[#0a2540]">{lead}</strong> {rest}</span>
                </li>
              ))}
            </ul>

            <div className="mt-14 bg-white border border-[#ffde59]/50 rounded-2xl p-6 md:p-8 max-w-2xl mx-auto shadow-sm">
              <i className="fa-solid fa-quote-left text-2xl text-[#ffde59] mb-4 block" aria-hidden="true" />
              <p className="font-serif text-lg md:text-xl text-[#0a2540] leading-snug">
                Property should never be treated as just a transaction.
              </p>
              <p className="mt-3 text-sm md:text-base text-slate-500">
                It should be approached as a long-term wealth strategy, lifestyle planning tool, and retirement roadmap.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =================== CLARITY SYSTEM =================== */}
      <section id="clarity-system" className="bg-white py-20 md:py-28" data-testid="framework-clarity">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>The Core System</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              The <span className="italic">C.L.A.R.I.T.Y</span> System
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              Seven steps. One outcome: clarity.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mb-12 max-w-5xl mx-auto">
            <Artwork
              src={frameworkArtwork.clarity}
              alt="The C.L.A.R.I.T.Y System — seven-step strategic property framework"
            />
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {claritySteps.map((s, i) => (
              <motion.div
                key={s.letter + i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
                className="relative bg-[#fafafa] border border-gray-100 rounded-2xl p-7 hover:bg-white hover:shadow-lg hover:-translate-y-1 transition-all"
              >
                <div className="absolute -top-4 left-7 h-10 w-10 rounded-full inline-flex items-center justify-center font-serif text-xl font-bold bg-[#ffde59] text-[#023e8a] shadow-md">
                  {s.letter}
                </div>
                <div className="pt-4">
                  <div className="font-serif text-lg text-[#0a2540] mb-2 leading-tight">{s.word}</div>
                  <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== WATER CONCEPT =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="framework-water">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <Reveal className="text-center">
              <Eyebrow>The Concept</Eyebrow>
              <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
                The <span className="italic">W.A.T.E.R</span> Concept
              </h2>
              <p className="mt-3 text-base md:text-lg font-serif italic text-[#0a2540]/80">
                Wealth Accumulation Towards Early Retirement.
              </p>
              <p className="mt-6 text-base text-slate-600 leading-relaxed">
                The core philosophy behind our property wealth planning. Rather than emotional or trend-driven decisions, this concept focuses on building a structured property roadmap that supports long-term financial independence and retirement readiness.
              </p>
              
              <ul className="mt-7 space-y-3 text-left max-w-2xl mx-auto">
                {[
                  "Build sustainable retirement income streams",
                  "Grow property equity progressively over time",
                  "Strengthen long-term net worth positioning",
                  "Improve financial flexibility and lifestyle options",
                  "Create strategic entry and exit plans across life stages",
                  "The W.A.T.E.R Concept emphasizes disciplined decision-making, long-term planning, and structured asset progression through Singapore real estate."
                ].map((b) => (
                  <li key={b} className="flex items-start gap-3 text-sm md:text-base text-slate-700">
                    <i className="fa-solid fa-circle-check mt-1 text-[#023e8a]" aria-hidden="true" />
                    {b}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.1} className="mt-14 max-w-5xl mx-auto">
              <Artwork
                src={frameworkArtwork.water}
                alt="The W.A.T.E.R Concept — Wealth Accumulation Towards Early Retirement"
              />
            </Reveal>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {waterPillars.map((p, i) => (
              <motion.div
                key={p.letter + i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.06 }}
                className="bg-white border border-gray-100 rounded-2xl p-6 text-center hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="font-serif text-5xl text-[#0a2540] mb-3 leading-none">{p.letter}</div>
                <div className="font-serif text-base text-[#0a2540] mb-2 leading-tight">{p.word}</div>
                <p className="text-xs text-slate-600 leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* =================== CLEAR FRAMEWORK =================== */}
      <section className="bg-white py-20 md:py-28" data-testid="framework-clear">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>The Framework</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              The <span className="italic">C.L.E.A.R</span> Framework
            </h2>
            <p className="mt-3 text-base md:text-lg font-serif italic text-[#0a2540]/80">
              A Structured Process for Property Decision-Making.
            </p>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              The C.L.E.A.R Framework is a strategic decision framework that guides clients through different stages of the property planning journey. It helps simplify complex property decisions into a more structured and financially sustainable process — transforming property planning from emotional decision-making into a more strategic and data-supported process.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mb-14 max-w-5xl mx-auto">
            <Artwork
              src={frameworkArtwork.clear}
              alt="The C.L.E.A.R Framework — five-step roadmap for confident property decisions"
            />
          </Reveal>

          <div className="relative">
            <div className="hidden lg:block absolute top-12 left-12 right-12 h-px bg-gradient-to-r from-transparent via-[#ffde59] to-transparent" />
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
              {clearSteps.map((s, i) => (
                <motion.div
                  key={s.letter + i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="text-center group"
                >
                  <div className="relative h-24 w-24 rounded-full bg-white mx-auto mb-5 inline-flex items-center justify-center font-serif text-4xl font-bold text-[#0a2540] border-2 border-[#ffde59] shadow-md group-hover:scale-105 transition-transform">
                    {s.letter}
                  </div>
                  <div className="font-serif text-lg text-[#0a2540] mb-2 leading-tight">{s.word}</div>
                  <p className="text-xs text-slate-600 leading-relaxed mb-4">{s.desc}</p>
                  <div className="inline-block px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-[#0a2540] bg-[#fffbea] border border-[#ffde59]/40 rounded-full">
                    {s.tag}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =================== LEX METHOD =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="framework-lex">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-3xl mx-auto mb-12">
            <Eyebrow>The Method</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              The <span className="italic">L.E.X</span> Method
            </h2>
            <p className="mt-3 text-base md:text-lg font-serif italic text-[#0a2540]/80">
              A Risk-Managed Approach to Property Selection.
            </p>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              The L.E.X Method is designed to help clients evaluate property opportunities more strategically before making a commitment. Every property is analysed across three key pillars to improve long-term positioning and reduce unnecessary downside risk — guided by strategy, positioning, and long-term market sustainability, not short-term hype.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mb-14 max-w-5xl mx-auto">
            <Artwork
              src={frameworkArtwork.lex}
              alt="The L.E.X Method — Location, Entry Price, Exit Strategy"
            />
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6">
            {lexPillars.map((p, i) => (
              <motion.div
                key={p.letter}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="bg-white border border-gray-100 rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <div className="font-serif text-7xl text-[#0a2540] leading-none mb-3">{p.letter}</div>
                <div className="font-serif text-xl text-[#0a2540] mb-4 leading-snug">
                  {p.word}
                </div>
                <div className="h-px bg-gradient-to-r from-[#ffde59] to-transparent mb-5" />
                <p className="text-sm text-slate-600 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            ))}
          </div>

          <Reveal delay={0.15} className="mt-12">
            <div className="bg-white border border-gray-100 rounded-3xl p-8 md:p-10 text-center max-w-3xl mx-auto">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0a2540]/70 font-semibold mb-3">
                LEX Today. Freedom Tomorrow.
              </div>
              <p className="font-serif text-xl md:text-2xl text-[#0a2540] leading-snug">
                A disciplined approach to property selection — built on strategy, backed by data, focused on your future.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* =================== STRATEGIC PROPERTY WEALTH PLANNING =================== */}
      <section className="bg-white py-20 md:py-28" data-testid="framework-services">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center max-w-4xl mx-auto mb-14">
            <div className="flex flex-col items-center justify-center mb-5">
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#cfa94a] font-semibold mb-2">
                WHAT WE HELP YOU PLAN
              </div>
              <div className="flex items-center w-full max-w-[5rem] mx-auto">
                <div className="flex-grow h-px bg-[#cfa94a]"></div>
                <div className="text-[8px] text-[#cfa94a] px-2">♦</div>
                <div className="flex-grow h-px bg-[#cfa94a]"></div>
              </div>
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.1]">
              Strategic <span className="italic">Property Wealth</span> Planning<span className="text-[#cfa94a]">.</span>
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              We believe successful property investing is not about chasing headlines or following market emotions.
              It is about having <br /><b>Clarity, Structure, Risk Management, Strategic Positioning and Long-Term Planning</b>.<br/>
              Because the right property decision today can shape your financial flexibility for decades ahead.
              At MeProperty, we focus on helping clients navigate.
            </p>
          </Reveal>

          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {strategicServices.map((s, i) => (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
                className="advisory-card bg-white border border-gray-100 rounded-2xl p-5 hover:shadow-lg transition-all flex flex-col sm:flex-row items-center sm:items-start text-center sm:text-left gap-4 sm:gap-6"
              >
                <div className="w-full sm:w-2/5 shrink-0 flex justify-center">
                  <img
                    src={s.img}
                    alt={`${s.title} graphic`}
                    loading="lazy"
                    className="w-full max-w-[180px] sm:max-w-full h-auto object-contain drop-shadow-sm advisory-icon"
                  />
                </div>
                <div className="w-full sm:w-3/5 flex flex-col items-center sm:items-start pt-2">
                  <div className="h-10 w-10 rounded-full bg-[#0a2540] inline-flex items-center justify-center mb-3 shadow-md shrink-0">
                    <i className={`${s.icon} text-[#ffde59] text-sm`} aria-hidden="true" />
                  </div>
                  <h3 className="font-serif text-lg sm:text-xl text-[#0a2540] mb-2 leading-tight">{s.title}</h3>
                  <p className="text-[13px] sm:text-sm text-slate-600 leading-relaxed">{s.desc}</p>
                </div>
              </motion.article>
            ))}
          </div>

          <Reveal delay={0.2} className="mt-5">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.5 }}
              className="advisory-card bg-white border border-gray-100 rounded-2xl p-5 md:p-8 hover:shadow-lg transition-all flex flex-col md:flex-row items-center md:items-start text-center md:text-left gap-6 md:gap-10"
            >
              <div className="w-full md:w-[380px] shrink-0 flex justify-center items-center">
                <img
                  src="/FrameworkPage_Strategic Property Wealth Planning-Section-icons/icon1.png"
                  alt="Strategic Property Entry & Exit illustration"
                  loading="lazy"
                  className="w-full max-w-[320px] md:max-w-full h-auto object-contain drop-shadow-sm advisory-icon"
                />
              </div>
              <div className="flex-1 flex flex-col pt-2 md:pt-4">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 md:gap-6 mb-4 text-center sm:text-left">
                  <img
                    src="/FrameworkPage_Strategic Property Wealth Planning-Section-icons/icon2.png"
                    alt="Entry & Exit Planning Icon"
                    loading="lazy"
                    className="h-16 w-16 md:h-[72px] md:w-[72px] object-contain shrink-0 drop-shadow-sm"
                  />
                  <div className="flex flex-col items-center sm:items-start pt-1 md:pt-2">
                    <h3 className="font-serif text-[22px] sm:text-2xl lg:text-[26px] text-[#0a2540] leading-tight mb-3">
                      Strategic Property Entry &amp; Exit Planning
                    </h3>
                    <div className="w-12 h-[2px] bg-[#d4af37] mx-auto sm:mx-0" />
                  </div>
                </div>
                <p className="text-[15px] sm:text-base text-slate-600 leading-relaxed max-w-3xl text-center sm:text-left sm:ml-[88px] md:ml-[96px]">
                  Enter the right property at the right time with a clear strategy.<br className="hidden lg:block" />
                  Plan your exit with precision to lock in gains and move forward.
                </p>
              </div>
            </motion.article>
          </Reveal>
        </div>
      </section>

      {/* =================== FINAL CTA =================== */}
      <section className="bg-[#fafafa] py-20 md:py-28" data-testid="framework-final-cta">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <Eyebrow>Your Next Move</Eyebrow>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0a2540] leading-[1.05]">
              Your next property decision should not be based on <span className="italic">fear, hype, or guesswork.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Whether you are planning to upgrade, invest, right-size, or restructure your portfolio, the goal is to create a roadmap aligned with your long-term financial objectives.
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Through the C.L.A.R.I.T.Y Property System, the W.A.T.E.R Concept, the C.L.E.A.R Framework, the L.E.X Method, and aligned with the M.E Roadmap, we help clients approach Singapore real estate with greater structure, confidence, and strategic direction.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link
                to="/contact"
                data-testid="framework-cta-book"
                className="inline-flex items-center gap-2 bg-[#ffde59] text-[#023e8a] px-7 py-4 font-semibold text-base rounded-full hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Book a Private Property Strategy Consultation
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
              <a
                href={brand.whatsapp}
                target="_blank"
                rel="noreferrer"
                data-testid="framework-cta-whatsapp"
                className="inline-flex items-center gap-2 border border-[#0a2540]/20 text-[#0a2540] px-7 py-4 font-medium text-base rounded-full hover:border-[#0a2540] transition-all"
              >
                <i className="fa-brands fa-whatsapp" aria-hidden="true" />
                Speak With Johnny
              </a>
            </div>
            <p className="mt-12 text-xs uppercase tracking-[0.3em] font-semibold text-[#0a2540]/70">
              No pressure · No hard selling · Just structured, data-backed property planning tailored to your goals.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
